// TODO: MVP-9605 MVP-9538 - review scientific accuracy before go-live

export type IndicatorType = 'carbon' | 'water' | 'soil' | 'biodiversity' | 'social';

type Tier = {
  anchorVal: number;
  rangeMin: number;
  rangeMax: number | null;
  buildCopy: (nf: string) => string;
};

const TIERS: Record<IndicatorType, Tier[]> = {
  carbon: [
    {
      anchorVal: 0.022,
      rangeMin: 0.01,
      rangeMax: 20,
      buildCopy: (nf) =>
        `Each hectare stores as much carbon as ${nf} mature trees absorb in a full year — a living carbon bank growing silently in soil and biomass.`,
    },
    {
      anchorVal: 6.5,
      rangeMin: 3,
      rangeMax: 2000,
      buildCopy: (nf) =>
        `Each hectare holds the equivalent of ${nf} Europeans' entire annual carbon footprint — sequestered in living biomass and soil organic matter.`,
    },
    {
      anchorVal: 500,
      rangeMin: 250,
      rangeMax: null,
      buildCopy: (nf) =>
        `The carbon stock equals what ${nf} km of daily motorway traffic emits in a year — locked away rather than released into the atmosphere.`,
    },
  ],
  water: [
    {
      anchorVal: 0.13,
      rangeMin: 0.05,
      rangeMax: 100,
      buildCopy: (nf) =>
        `The land retains enough water to meet ${nf} people's daily needs — held in soil and wetlands rather than running off into drains.`,
    },
    {
      anchorVal: 5,
      rangeMin: 2,
      rangeMax: 5000,
      buildCopy: (nf) =>
        `That's the equivalent of ${nf} full rainwater harvest tanks absorbed by the land — slowly recharging groundwater and supporting dry-season flows.`,
    },
    {
      anchorVal: 6000,
      rangeMin: 3000,
      rangeMax: null,
      buildCopy: (nf) =>
        `The site retains the equivalent of ${nf} years' worth of rainfall over a single hectare — a deep, slow-moving reservoir held beneath the surface.`,
    },
  ],
  soil: [
    {
      anchorVal: 1,
      rangeMin: 0.5,
      rangeMax: 500,
      buildCopy: (nf) =>
        `Each hectare holds the organic equivalent of ${nf} tonnes of manure — locked into the soil structure rather than spread on the surface.`,
    },
    {
      anchorVal: 30,
      rangeMin: 15,
      rangeMax: 15000,
      buildCopy: (nf) =>
        `Topsoil carbon is running at ${nf}× the EU average for comparable arable land — a foundation most farms spend decades trying to build.`,
    },
    {
      anchorVal: 15,
      rangeMin: 7,
      rangeMax: null,
      buildCopy: (nf) =>
        `The estate's topsoil stores the equivalent of ${nf} lorry loads of rich compost — accumulated over years, protecting productivity from drought and erosion.`,
    },
  ],
  biodiversity: [],
  social: [],
};

const formatN = (n: number) => {
  if (n >= 1000) return Math.round(n).toLocaleString();
  if (n >= 10) return Math.round(n).toLocaleString();
  if (n >= 1) return parseFloat(n.toFixed(1)).toLocaleString();
  return parseFloat(n.toFixed(2)).toLocaleString();
};

const pickTier = (tiers: Tier[], value: number) => {
  const matched = tiers.find((tier) => value >= tier.rangeMin && (tier.rangeMax === null || value <= tier.rangeMax));
  if (matched) return matched.buildCopy(formatN(value / matched.anchorVal));

  const best =
    tiers.reduce<{ tier: Tier; dist: number } | null>((acc, tier) => {
      const mid = tier.rangeMax !== null ? (tier.rangeMin + tier.rangeMax) / 2 : tier.rangeMin * 10;
      const dist = Math.abs(Math.log10(value + 0.001) - Math.log10(mid + 0.001));
      return acc === null || dist < acc.dist ? { tier, dist } : acc;
    }, null)?.tier ?? null;

  return best ? best.buildCopy(formatN(value / best.anchorVal)) : null;
};

const carbonCopy = (value: number): string | null => {
  return pickTier(TIERS.carbon, value);
};

const waterCopy = (value: number): string | null => {
  return pickTier(TIERS.water, value);
};

const soilCopy = (value: number): string | null => {
  return pickTier(TIERS.soil, value);
};

const biodiversityCopy = (value: number): string | null => {
  if (value < 0 || value > 100) return null;
  const diff58 = value - 58;
  const diff90 = value - 90;
  const usePlanetarySafety = Math.abs(diff58) <= Math.abs(diff90);
  const diff = usePlanetarySafety ? diff58 : diff90;
  const pts = Math.abs(diff).toFixed(1);
  if (usePlanetarySafety) {
    return diff >= 0
      ? `At ${value}%, biodiversity intactness sits ${pts} points above the planetary safety boundary — the level scientists identify as the threshold for rising ecological collapse risk.`
      : `At ${value}%, biodiversity intactness sits ${pts} points below the planetary safety boundary — a level at which ecological function and resilience begin to break down.`;
  }
  return diff >= 0
    ? `At ${value}%, this land matches the intactness you'd find inside a formally protected nature reserve — where most native species communities are still functioning at close to their natural baseline.`
    : `At ${value}%, intactness is ${pts} points short of what you'd expect inside a formally protected nature reserve — meaningful progress, with room still to recover.`;
};

const socialCopy = (value: number): string | null => {
  if (value < 0) return null;
  return `Communities near managed sites live ${value.toFixed(1)} years longer than the regional baseline — an uplift comparable to the life-expectancy benefit of everyone within reach taking a daily 30-minute walk.`;
};

export const getIndicatorCopy = (indicator: IndicatorType, value: number): string | null => {
  if (!Number.isFinite(value)) return null;
  switch (indicator) {
    case 'carbon':
      return carbonCopy(value);
    case 'water':
      return waterCopy(value);
    case 'soil':
      return soilCopy(value);
    case 'biodiversity':
      return biodiversityCopy(value);
    case 'social':
      return socialCopy(value);
    default:
      return null;
  }
};
